import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Pencil, Trash2, Plus, ArrowLeft } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

function AssessmentManager() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [newAssessment, setNewAssessment] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/assessments');
      setAssessments(response.data);
    } catch (error) {
      toast.error('Erro ao carregar avaliações');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssessment = async () => {
    if (!newAssessment.name.trim()) {
      toast.error('Nome da avaliação é obrigatório');
      return;
    }

    try {
      await api.post('/assessments', newAssessment);
      toast.success('Avaliação criada com sucesso!');
      setOpenCreate(false);
      setNewAssessment({ name: '', description: '' });
      fetchAssessments();
    } catch (error) {
      toast.error('Erro ao criar avaliação');
      console.error(error);
    }
  };

  const handleDeleteAssessment = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação?')) {
      return;
    }

    try {
      await api.delete(`/assessments/${id}`);
      toast.success('Avaliação excluída com sucesso!');
      fetchAssessments();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Não é possível excluir esta avaliação');
      } else {
        toast.error('Erro ao excluir avaliação');
      }
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gerenciar Avaliações</h1>
              <p className="text-gray-600">Crie e gerencie diferentes tipos de avaliações DISC</p>
            </div>
          </div>

          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 h-4 w-4" />
                Nova Avaliação
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Nova Avaliação</DialogTitle>
                <DialogDescription>
                  Preencha os detalhes da avaliação. Você poderá adicionar perguntas depois.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="name">Nome da Avaliação *</Label>
                  <Input
                    id="name"
                    placeholder="Ex: DISC Completo, DISC Simplificado"
                    value={newAssessment.name}
                    onChange={(e) => setNewAssessment({ ...newAssessment, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    placeholder="Descreva o propósito desta avaliação..."
                    value={newAssessment.description}
                    onChange={(e) => setNewAssessment({ ...newAssessment, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setOpenCreate(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateAssessment} className="bg-emerald-600 hover:bg-emerald-700">
                    Criar Avaliação
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Assessment List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Carregando...</p>
          </div>
        ) : assessments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600 mb-4">Nenhuma avaliação criada ainda</p>
              <Button onClick={() => setOpenCreate(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 h-4 w-4" />
                Criar Primeira Avaliação
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {assessments.map((assessment) => (
              <Card key={assessment.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{assessment.name}</CardTitle>
                      <CardDescription className="mt-2">
                        {assessment.description || 'Sem descrição'}
                      </CardDescription>
                      <p className="text-sm text-gray-500 mt-2">
                        {assessment.questions?.length || 0} perguntas cadastradas
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/assessments/${assessment.id}/questions`)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Gerenciar Perguntas
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteAssessment(assessment.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AssessmentManager;
